import { nodeConfig } from '@scripts/mixins';
import * as CONSTANTS from '@scripts/helpers/constants';
import { Announcer } from '@scripts/helpers/announcer';
import { Logger } from '@scripts/mixins';

import EmblaCarousel from 'embla-carousel';
import AutoHeight from "embla-carousel-auto-height";
import Accessibility from "embla-carousel-accessibility";
import ClassNames from "embla-carousel-class-names";
import WheelGestures from "embla-carousel-wheel-gestures";
import { cloneDeep } from 'lodash';

export const NAMESPACE = 'embla-carousel';
export const DEFAULT_SELECTOR = '[data-js-embla-carousel]';

const AVAILABLE_PLUGINS = {
	'AutoHeight': AutoHeight,
	'Accessibility': Accessibility,
	'ClassNames': ClassNames,
	'WheelGestures': WheelGestures,
};

/**
 * @typedef {Object} CarouselSettings
 * @property {string} [align='start'] - Alignment of the slides within the carousel.
 * @property {boolean} [active=true] - Whether the carousel is active.
 * @property {Object<string, Object>} [plugins={}] - Array of Embla carousel plugins to use.
 */
export const DEFAULT_SETTINGS = {
	align: 'start',
	active: true,
	plugins: {},
};

/**
 * Accessible Swiper carousel wrapper.
 * @fires carousel:slideVisible
 * @fires carousel:slideHidden
 */
export class CarouselEmbla {
	/**
	 * @param {HTMLElement} node
	 * @param {CarouselSettings} config
	 */
	constructor(node, config) {
		this.elNode = node;
		this.config = config;
		this.elViewport = Array.from(node.querySelectorAll(`[data-js-${NAMESPACE}-viewport]`))
			.filter((el) => el.closest(`[data-js-${NAMESPACE}]`) === this.elNode)
			.at(0);
		this.elPagination = Array.from(node.querySelectorAll(`[data-js-${NAMESPACE}-pagination]`))
			.filter((el) => el.closest(`[data-js-${NAMESPACE}]`) === this.elNode)
			.at(0);
		this.elArrowPrev = Array.from(node.querySelectorAll(`[data-js-${NAMESPACE}-arrow-prev]`))
			.filter((el) => el.closest(`[data-js-${NAMESPACE}]`) === this.elNode)
			.at(0);
		this.elArrowNext = Array.from(node.querySelectorAll(`[data-js-${NAMESPACE}-arrow-next]`))
			.filter((el) => el.closest(`[data-js-${NAMESPACE}]`) === this.elNode)
			.at(0);
		this.elSlides = Array.from(node.querySelectorAll(`[data-js-${NAMESPACE}-slide]`))
			.filter((el) => el.closest(`[data-js-${NAMESPACE}]`) === this.elNode);
		this.elCounter = Array.from(node.querySelectorAll(`[data-js-${NAMESPACE}-counter]`))
			.filter((el) => el.closest(`[data-js-${NAMESPACE}]`) === this.elNode)
			.at(0);
		this._isInteracted = false;
		this.api = null;
		this.enabledPlugins = [];
		this.isLocked = false;
	}

	get _elPaginationDots() {
		if (!this.elPagination) return [];
		return Array.from(this.elPagination.querySelectorAll('.embla__dot'));
	}

	init() {
		this._validateCarousel();
		this._setup();
		this._bindUI();
	}

	_validateCarousel() {
		if (!this.elViewport) {
			Logger.error('Embla carousel viewport not found.');
		}

		if (!this.elSlides.length) {
			Logger.error('Embla carousel slides not found.');
		}

		if (this.elSlides.some((el) => !el.hasAttribute('id'))) {
			Logger.warn('Embla carousel slides should have an id attribute.');
		}
	}

	_setup() {
		this.config.plugins['ClassNames'] = {};

		const pluginArray = Object.keys(this.config.plugins);
		this.enabledPlugins = pluginArray.filter((pluginName) => Object.hasOwn(AVAILABLE_PLUGINS, pluginName));

		const configClone = cloneDeep(this.config);
		delete configClone.plugins;

		const emblaParams = {
			...configClone,
			inViewThreshold: 0.01,
		};

		this.api = EmblaCarousel(
			this.elViewport,
			emblaParams,
			this.enabledPlugins.map((pluginName) => {
				const pluginFunction = AVAILABLE_PLUGINS[pluginName];
				const pluginConfig = this.config.plugins[pluginName] || {};
				return pluginFunction(pluginConfig);
			})
		);

		this._createPaginationDots();
		this._setA11yAttributes();
		this._toggleArrowsDisabled();
		this._toggleLockState();
		this._setCounterText();
	}

	_bindUI() {
		this.elArrowPrev?.addEventListener('click', this.goToPrev.bind(this), false);
		this.elArrowNext?.addEventListener('click', this.goToNext.bind(this), false);
		this.elPagination?.addEventListener('click', this._handlePaginationClick.bind(this), false);
		this.api.on('reinit', this._createPaginationDots.bind(this));
		this.api.on('reinit', this._toggleArrowsDisabled.bind(this));
		this.api.on('reinit', this._toggleLockState.bind(this));
		this.api.on('reinit', this._setCounterText.bind(this));
		this.api.on('select', this._togglePaginationDotsActive.bind(this));
		this.api.on('select', this._toggleArrowsDisabled.bind(this));
		this.api.on('select', this._setCounterText.bind(this));
		this.api.on('slidesinview', this._handleSlidesInView.bind(this));
	}

	_toggleLockState() {
		this.isLocked = !this.api.internalEngine().options.active || (!this.api.canGoToNext() && !this.api.canGoToPrev());

		if (this.isLocked) {
			this.elNode.classList.add(CONSTANTS.LOCKED_CLASS);
			this._removeA11yAttributes();
		} else {
			this.elNode.classList.remove(CONSTANTS.LOCKED_CLASS);
			this._setA11yAttributes();
		}
	}

	_setA11yAttributes() {
		const elCarousel = this.elNode;
		const elContainer = this.api.containerNode();
		const elSlides = this.api.slideNodes();

		if (!elCarousel.hasAttribute('aria-labelledby') && !elCarousel.hasAttribute('aria-label')) {
			elCarousel.setAttribute('aria-label', 'Carousel');
		}

		if (elCarousel.nodeName.toLowerCase() !== 'section') {
			elCarousel.setAttribute('role', 'region');
		}

		elCarousel.setAttribute('aria-roledescription', 'carousel');

		elContainer.removeAttribute('role');

		elSlides.forEach((elSlide, i) => {
			elSlide.setAttribute('role', 'group');
			elSlide.setAttribute('aria-roledescription', 'slide');

			if (!elSlide.hasAttribute('aria-labelledby') && !elSlide.hasAttribute('aria-label')) {
				elSlide.setAttribute('aria-label', `${i + 1} of ${elSlides.length}`);
			}
		});

		this.elArrowPrev?.setAttribute('aria-label', 'Scroll to previous slide');
		this.elArrowNext?.setAttribute('aria-label', 'Scroll to next slide');
	}

	_removeA11yAttributes() {
		const elCarousel = this.elNode;
		const elContainer = this.api.containerNode();
		const elSlides = this.api.slideNodes();
		const hasMultipleSlides = elSlides.length > 1;

		elCarousel.removeAttribute('role');
		elCarousel.removeAttribute('aria-roledescription');

		if (hasMultipleSlides) {
			elContainer.setAttribute('role', 'list');
		}

		elSlides.forEach((elSlide) => {
			elSlide.removeAttribute('aria-roledescription');

			if (hasMultipleSlides) {
				elSlide.setAttribute('role', 'listitem');
			} else {
				elSlide.removeAttribute('role');
			}
		});
	}

	_createPaginationDots() {
		if (!this.elPagination) return;

		const elSlides = this.api.slideNodes();
		const snapList = this.api.snapList();
		const snapSlides = this.api.internalEngine().scrollSnapList;
		this.elPagination.innerHTML = snapList.reduce((acc, v, i) => {
			const elDot = document.createElement('button');
			elDot.setAttribute('type', 'button');
			const firstSlideIndexInSnap = snapSlides.slidesBySnap[i][0];
			const elSlide = elSlides[firstSlideIndexInSnap];
			if (elSlide && elSlide.hasAttribute('id')) {
				elDot.setAttribute('aria-labelledby', elSlide.getAttribute('id'));
			} else if (elSlide) {
				elDot.setAttribute('aria-label', `Slide ${firstSlideIndexInSnap + 1}`);
			}

			elDot.classList.add('embla__dot');
			return acc + elDot.outerHTML;
		}, '');

		this._togglePaginationDotsActive();
	}

	_togglePaginationDotsActive() {
		if (!this.elPagination) return;

		const previous = this.api.previousSnap();
		const selected = this.api.selectedSnap();

		const elDots = this._elPaginationDots;
		if (!elDots.length) return;

		elDots[previous]?.classList.remove('embla__dot--selected')
		elDots[previous]?.removeAttribute('aria-disabled');
		elDots[selected]?.classList.add('embla__dot--selected');
		elDots[selected]?.setAttribute('aria-disabled', 'true');
	}

	_handlePaginationClick(event) {
		const target = event.target;
		if (!target || !target.closest('button')) return;

		const buttons = Array.from(this.elPagination.querySelectorAll('button'));
		const index = buttons.indexOf(target.closest('button'));
		if (index === -1) return;

		this.goTo(index);
	}

	_toggleArrowsDisabled() {
		if (!this.elArrowPrev || !this.elArrowNext) return;

		this.elArrowPrev.toggleAttribute('disabled', !this.api.canGoToPrev());
		this.elArrowNext.toggleAttribute('disabled', !this.api.canGoToNext());
	}

	_manageSlideFocus(item, isVisible) {
		const focusables = item.querySelectorAll(CONSTANTS.FOCUSABLE_ELEMENTS_SELECTOR);

		focusables.forEach((el) => {
			if (!isVisible) {
				if (!el.hasAttribute('data-orig-tabindex')) {
					const currentTabindex = el.getAttribute('tabindex');

					el.setAttribute(
						'data-orig-tabindex',
						currentTabindex !== null ? currentTabindex : ''
					);
				}
				el.setAttribute('tabindex', '-1');
			} else if (el.hasAttribute('data-orig-tabindex')) {
				const original = el.getAttribute('data-orig-tabindex');

				if (original === '') {
					el.removeAttribute('tabindex');
				} else {
					el.setAttribute('tabindex', original);
				}

				el.removeAttribute('data-orig-tabindex');
			}
		});
	}

	_handleSlidesInView(api, event) {
		const { slidesInView } = event.detail;

		const slides = api.slideNodes();
		slides.forEach((slide, index) => {
			this._manageSlideFocus(slide, slidesInView.includes(index));
			slide.setAttribute('aria-hidden', !slidesInView.includes(index));
		});
	}

	_setCounterText() {
		if (!this.elCounter) return;

		const selected = this.api.selectedSnap();
		const total = this.api.snapList().length;

		this.elCounter.textContent = `${selected + 1} / ${total}`;
	}

	goTo(index) {
		if (!this.api) return;

		this.api.goTo(index);
	}

	goToSlide(index) {
		if (!this.api) return;

		const snapSlides = this.api.internalEngine().scrollSnapList.slidesBySnap;
		const matchedSnap = snapSlides.findIndex((snap) => snap.includes(index));
		if (matchedSnap === -1) return;

		this.api.goTo(matchedSnap);
	}

	goToNext() {
		if (!this.api) return;

		this.api.goToNext();
	}

	goToPrev() {
		if (!this.api) return;

		this.api.goToPrev();
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		const module = new CarouselEmbla(node, config);

		module.init();

		return module;
	});
}